/**
 * Hook useKanbanConversationRealtime
 *
 * Subscription Realtime ÚNICA para o Kanban de Conversas — substitui as
 * subscriptions por coluna (2 canais × N colunas) por um único canal
 * por aba/tenant. Aplica patch otimista in-cache para eventos comuns e
 * coalesce rajadas via debounce para evitar avalanche de RPCs.
 *
 * CHANGELOG:
 *   v1.1.0 - 2026-06-26 — Patch otimista agora REORDENA a conversa para
 *            o topo da primeira página quando last_message_at fica mais
 *            recente (mensagem nova em conversa já visível). Itens que
 *            estavam em página > 1 caem no fluxo de refetch debounced.
 *   v1.0.0 - 2026-06-26 — Versão inicial.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTenantInboxIds, isFromCurrentTenant } from '@/hooks/useTenantInboxIds';
import type { KanbanConversationItem } from '@/hooks/useKanbanConversationColumn';

const logger = createLogger('KanbanConversationRealtime');

// Janela de coalescing — agrupa rajadas em um único ciclo de refetch
const DEBOUNCE_MS = 1500;

/**
 * Hook montado UMA VEZ no KanbanBoard (no nível da categoria).
 *
 * @param categoryId - categoria do Kanban (usada para invalidar stats)
 * @param enabled - permite desligar a subscription (default true)
 */
export function useKanbanConversationRealtime(
  categoryId: string | null,
  enabled: boolean = true,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const inboxIdsRef = useTenantInboxIds();

  // Fases que precisam de refetch (acumulam até o debounce disparar)
  const pendingPhasesRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statsDirtyRef = useRef(false);

  useEffect(() => {
    if (!currentTenant?.id || !enabled) return;

    /**
     * Aplica patch otimista no cache de TODAS as variações de filtro de
     * uma fase. Procura a conversa pelo id em cada página do InfiniteData
     * e atualiza os campos voláteis. Quando o last_message_at do patch é
     * mais recente que o atual, REMOVE o item da posição original e o
     * REINSERE no topo da primeira página (mantém UX "novidade no topo").
     *
     * Retorna `true` quando aplicou (item localizado em qualquer página),
     * `false` quando o item não está em nenhuma página em cache.
     */
    const patchConversationInCache = (
      phaseId: string,
      conversationId: string,
      patch: Partial<KanbanConversationItem>,
    ): boolean => {
      let patched = false;

      const queries = queryClient.getQueriesData<InfiniteData<KanbanConversationItem[]>>({
        queryKey: ['kanban-conversation-column', phaseId],
      });

      queries.forEach(([key, data]) => {
        if (!data?.pages?.length) return;

        // Localiza item e descobre se a nova mensagem é mais recente
        let foundItem: KanbanConversationItem | null = null;
        let foundPageIdx = -1;
        for (let i = 0; i < data.pages.length; i++) {
          const idx = data.pages[i].findIndex((c) => c.id === conversationId);
          if (idx >= 0) {
            foundItem = data.pages[i][idx];
            foundPageIdx = i;
            break;
          }
        }

        if (!foundItem) return;

        patched = true;
        const merged = { ...foundItem, ...patch };

        // Decide se precisa reordenar (mensagem nova → topo)
        const oldTs = foundItem.last_message_at ? Date.parse(foundItem.last_message_at) : 0;
        const newTs = merged.last_message_at ? Date.parse(merged.last_message_at) : 0;
        const shouldReorder = newTs > oldTs;

        if (!shouldReorder) {
          // Patch in-place sem mexer na ordem
          const newPages = data.pages.map((page) =>
            page.map((c) => (c.id === conversationId ? merged : c)),
          );
          queryClient.setQueryData(key, { ...data, pages: newPages });
          return;
        }

        // Reordena: remove de onde estava, insere no topo da página 0
        const newPages = data.pages.map((page, i) => {
          const filtered = i === foundPageIdx ? page.filter((c) => c.id !== conversationId) : page;
          return i === 0 ? [merged, ...filtered] : filtered;
        });
        queryClient.setQueryData(key, { ...data, pages: newPages });
      });

      return patched;
    };

    /**
     * Dispara invalidação debounced — uma única vez por fase afetada,
     * mais os stats se algum evento estrutural ocorreu.
     */
    const scheduleFlush = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      debounceTimerRef.current = setTimeout(() => {
        const phases = Array.from(pendingPhasesRef.current);
        pendingPhasesRef.current.clear();

        if (phases.length > 0) {
          logger.debug(`Flush debounced: invalidando ${phases.length} fase(s)`, phases);
          phases.forEach((phaseId) => {
            queryClient.invalidateQueries({
              queryKey: ['kanban-conversation-column', phaseId],
            });
          });
        }

        if (statsDirtyRef.current) {
          statsDirtyRef.current = false;
          queryClient.invalidateQueries({
            queryKey: ['kanban-conversation-phase-stats'],
          });
        }
      }, DEBOUNCE_MS);
    };

    /** Marca uma fase como "precisa refetch" e agenda flush. */
    const markPhaseDirty = (phaseId: string | null | undefined, statsToo = false) => {
      if (phaseId) pendingPhasesRef.current.add(phaseId);
      if (statsToo) statsDirtyRef.current = true;
      scheduleFlush();
    };

    logger.debug(`Configurando Realtime unificado para categoria ${categoryId}`);

    const channel = supabase
      .channel(`kanban-conv-board-${currentTenant.id}-${categoryId ?? 'all'}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        (payload) => {
          // Descarta eventos de outros tenants
          if (!isFromCurrentTenant(inboxIdsRef, payload)) return;

          // Descarta eventos enquanto aba está oculta — visibilitychange
          // dispara invalidação geral ao voltar ao foreground.
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return;
          }

          const eventType = payload.eventType;
          const oldRow = payload.old as Partial<KanbanConversationItem> | undefined;
          const newRow = payload.new as Partial<KanbanConversationItem> | undefined;

          if (eventType === 'INSERT') {
            // Nova conversa: precisa refetch da fase destino + stats
            markPhaseDirty(newRow?.kanban_phase_id, true);
            return;
          }

          if (eventType === 'DELETE') {
            markPhaseDirty(oldRow?.kanban_phase_id, true);
            return;
          }

          // UPDATE
          const oldPhase = oldRow?.kanban_phase_id ?? null;
          const newPhase = newRow?.kanban_phase_id ?? null;
          const phaseChanged = oldPhase !== newPhase;

          if (phaseChanged) {
            // Movimentação entre fases: refetch das duas + stats
            markPhaseDirty(oldPhase, true);
            markPhaseDirty(newPhase, true);
            return;
          }

          // Mesma fase: tenta patch otimista (caso comum: mensagem nova)
          if (newPhase && newRow?.id) {
            const patched = patchConversationInCache(newPhase, newRow.id, {
              last_message_at: newRow.last_message_at ?? null,
              last_message_preview: newRow.last_message_preview ?? null,
              unread_count: newRow.unread_count ?? 0,
              status: newRow.status ?? '',
              assigned_to: newRow.assigned_to ?? null,
            });

            if (!patched) {
              // Conversa não está no cache (paginação fora da janela)
              // → refetch da fase sem mexer nos stats.
              markPhaseDirty(newPhase, false);
            }
          }
        },
      )
      .subscribe();

    // Visibilidade: ao voltar para a aba, faz 1 invalidação geral
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        logger.debug('Aba voltou ao foreground — invalidando colunas e stats');
        queryClient.invalidateQueries({ queryKey: ['kanban-conversation-column'] });
        queryClient.invalidateQueries({ queryKey: ['kanban-conversation-phase-stats'] });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      logger.debug('Removendo Realtime unificado do Kanban de Conversas');
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, categoryId, enabled, queryClient, inboxIdsRef]);
}
